/**
 * API client for entry controls: machine-scoped entry, the entries list,
 * editing and deleting, settings, users, period reports and drill-down.
 */

import { ApiError, getToken, request } from "./api";
import type { Blocked, RunLine, Shift } from "./types";

const BASE =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.DEV ? "http://localhost:8000" : "");

// -- Entry context ----------------------------------------------------------

export type Direction = "INPUT" | "OUTPUT" | "WASTE";

export interface ContextItem {
  id: number;
  code: string;
  name: string;
  uom: string;
  item_type: string;
}

export interface ContextMachine {
  id: number;
  code: string;
  name: string;
  stage_type: string;
  tolerance_pct: string;
  /** Material codes per direction; null means no list yet, so anything. */
  materials: Record<Direction, string[] | null>;
}

export interface EntryContext {
  machines: ContextMachine[];
  items: ContextItem[];
  reason_codes: { id: number; code: string; description: string; requires_note: boolean }[];
  operators: { id: number; code: string; name: string }[];
  parties: { id: number; code: string; name: string }[];
  looms: { loom_no: number; party_id: number | null; model: string | null }[];
  can_change_any: boolean;
}

/** The materials a machine offers for one direction, as full items. */
export function materialsFor(
  ctx: EntryContext | null,
  machine: ContextMachine | undefined,
  direction: Direction
): ContextItem[] {
  if (!ctx) return [];
  const codes = machine?.materials[direction];
  if (!codes) {
    // No list yet: everything, with waste-type materials first for waste.
    if (direction !== "WASTE") return ctx.items;
    return [...ctx.items].sort(
      (a, b) =>
        Number(b.item_type === "WASTE") - Number(a.item_type === "WASTE")
    );
  }
  const byCode = new Map(ctx.items.map((i) => [i.code, i]));
  return codes.map((c) => byCode.get(c)).filter(Boolean) as ContextItem[];
}

// -- Entries ----------------------------------------------------------------

export interface EntryRow {
  id: number;
  business_date: string;
  shift: Shift;
  machine_code: string;
  machine_name: string;
  kind: "SHIFT" | "LOOM";
  status: string;
  running_hours: string | null;
  manpower_count: number | null;
  power_units: string | null;
  input_qty: string | null;
  output_qty: string | null;
  declared_loss: string | null;
  variance_qty: string | null;
  variance_pct: string | null;
  within_tolerance: boolean | null;
  reason: string | null;
  entered_by: string | null;
  edited: boolean;
  can_change: boolean;
}

export interface EntryLine {
  movement_id: number;
  type: string;
  item_code: string;
  item_name: string;
  qty: string;
  uom: string;
  from: string | null;
  to: string | null;
  loom_id: number | null;
}

export interface EntryLoom {
  loom_no: number;
  operator_id: number | null;
  operator_name: string | null;
  party_id: number | null;
  party_name: string | null;
  sku_code: string | null;
  input_item_code: string | null;
  input_kg: string | null;
  output_item_code: string | null;
  output_kg: string | null;
  qty_mtr: string | null;
  rolls_count: number | null;
  waste_kg: string | null;
  rolls: { roll_no: string; metres: string; kg: string | null }[];
}

export interface EntryDetail {
  id: number;
  kind: "SHIFT" | "LOOM";
  machine_code: string;
  machine_name: string;
  business_date: string;
  shift: Shift;
  status: string;
  running_hours: string | null;
  manpower_count: number | null;
  power_units: string | null;
  input_qty: string | null;
  output_qty: string | null;
  declared_loss: string | null;
  variance_qty: string | null;
  variance_pct: string | null;
  within_tolerance: boolean | null;
  tolerance_pct: string | null;
  reason_code_id: number | null;
  reason: string | null;
  variance_note: string | null;
  remarks: string | null;
  lines: EntryLine[];
  looms: EntryLoom[];
  entered_by: string | null;
  approved_by_name: string | null;
  edited_by_name: string | null;
  edited_at: string | null;
  deleted_by_name: string | null;
  delete_reason: string | null;
  can_change: boolean;
  cannot_change_because: string | null;
  history: {
    at: string;
    action: string;
    user: string | null;
    reason: string | null;
  }[];
}

export interface RollIn {
  roll_no: string;
  metres: number;
  kg?: number | null;
}

export interface LoomRowIn {
  loom_no: number;
  operator_id: number | null;
  party_id: number | null;
  input_item_code: string | null;
  input_kg: number | null;
  output_item_code: string | null;
  qty_kg: number | null;
  qty_mtr?: number;
  rolls: RollIn[];
}

export interface LoomShiftIn {
  business_date: string;
  shift: Shift;
  running_hours: number | null;
  manpower_count: number | null;
  power_units: number | null;
  /** Tape issued to the floor, for the whole shift. Not a per-loom figure. */
  input_kg: number | null;
  input_item_code: string | null;
  loss_kg: number | null;
  loss_item_code: string | null;
  remarks?: string | null;
  lines: LoomRowIn[];
  submit: boolean;
  reason_code_id: number | null;
  variance_note: string | null;
}

export interface StockWarning extends Blocked {}

export const controls = {
  context: () => request<EntryContext>("/api/entries/context"),

  entries: (from: string, to: string, includeDeleted = false, shift?: string) =>
    request<EntryRow[]>(
      `/api/entries?period_from=${from}&period_to=${to}` +
        (includeDeleted ? "&include_deleted=true" : "") +
        (shift ? `&shift=${shift}` : "")
    ),

  entry: (id: number) => request<EntryDetail>(`/api/entries/${id}`),

  editShift: (
    id: number,
    body: {
      machine_code: string;
      business_date: string;
      shift: Shift;
      running_hours: number | null;
      manpower_count: number | null;
      power_units: number | null;
      consume: RunLine[];
      produce: RunLine[];
      loss: RunLine[];
      edit_reason: string;
      reason_code_id: number | null;
      variance_note: string | null;
    }
  ) =>
    request<EntryDetail>(`/api/entries/${id}/shift`, { method: "PUT", body }),

  recordLoomShift: (body: LoomShiftIn) =>
    request<{
      run_id: number;
      looms_entered: number;
      total_rolls: number;
      total_mtr: string;
      total_kg: string;
      total_input_kg: string;
      status: string | null;
      variance_qty: string | null;
      within_tolerance: boolean | null;
      over_capacity: string[];
    }>("/api/looms/shift", { method: "POST", body, queueable: true }),

  editLoom: (id: number, editReason: string, shift: LoomShiftIn) =>
    request<EntryDetail>(`/api/entries/${id}/loom`, {
      method: "PUT",
      body: { edit_reason: editReason, shift },
    }),

  deleteEntry: (id: number, reason: string) =>
    request<{ id: number; status: string }>(`/api/entries/${id}/delete`, {
      method: "POST",
      body: { reason },
    }),

  // -- Settings -------------------------------------------------------------
  settings: () =>
    request<{ negative_stock_policy: "WARN" | "BLOCK" }>("/api/admin/settings"),

  saveSettings: (body: { negative_stock_policy?: string }) =>
    request<{ negative_stock_policy: "WARN" | "BLOCK" }>(
      "/api/admin/settings",
      { method: "PUT", body }
    ),

  adminMachines: () => request<AdminMachine[]>("/api/admin/machines"),

  saveMachine: (
    code: string,
    body: {
      tolerance_pct?: number;
      capacity_per_day?: number;
      materials?: Partial<Record<Direction, string[]>>;
    }
  ) =>
    request<AdminMachine>(`/api/admin/machines/${code}`, {
      method: "PUT",
      body,
    }),

  users: () => request<AdminUser[]>("/api/admin/users"),

  createUser: (body: {
    email: string;
    name: string;
    role: string;
    password: string;
    machines: string[];
  }) => request<AdminUser>("/api/admin/users", { method: "POST", body }),

  updateUser: (
    id: number,
    body: {
      name?: string;
      role?: string;
      active?: boolean;
      machines?: string[];
      password?: string;
    }
  ) => request<AdminUser>(`/api/admin/users/${id}`, { method: "PUT", body }),

  // -- Reports --------------------------------------------------------------
  production: (from: string, to: string, machines: string[] = [], shift?: string,
               compare: string = "prior") =>
    request<ProductionReport>(
      `/api/reports/production?period_from=${from}&period_to=${to}` +
        machines.map((m) => `&machine=${encodeURIComponent(m)}`).join("") +
        (shift ? `&shift=${shift}` : "") +
        (compare !== "prior" ? `&compare=${compare}` : "")
    ),

  efficiency: (from: string, to: string, shift?: string, compare: string = "prior") =>
    request<EfficiencyReport>(
      `/api/reports/efficiency?period_from=${from}&period_to=${to}` +
        (shift ? `&shift=${shift}` : "") +
        (compare !== "prior" ? `&compare=${compare}` : "")
    ),

  exceptions: (from: string, to: string, shift?: string, compare: string = "prior") =>
    request<ExceptionsReport>(
      `/api/reports/exceptions?period_from=${from}&period_to=${to}` +
        (shift ? `&shift=${shift}` : "") +
        (compare !== "prior" ? `&compare=${compare}` : "")
    ),

  drill: (spec: DrillSpec) =>
    request<DrillLevel>("/api/reports/drill", {
      method: "POST",
      body: { spec },
    }),
};

export async function uploadMachineMaterials(
  file: File,
  mode: "preview" | "commit"
): Promise<MaterialImport> {
  const form = new FormData();
  form.append("file", file);
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(
    `${BASE}/api/admin/machine-materials/${mode}`,
    { method: "POST", headers, body: form }
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload?.detail === "string"
        ? payload.detail
        : `Upload failed (${response.status})`
    );
  }
  return payload as MaterialImport;
}

export interface FactoryManualPlan {
  rows: number;
  saved: boolean;
  errors: string[];
  warnings: string[];
  sections: {
    section: string;
    users: string[];
    inputs: number;
    outputs: number;
    machines: string[];
  }[];
  new_items: { code: string; name: string; item_type: string; guessed: boolean }[];
  machines: Record<string, Record<Direction, string[]>>;
  machines_untouched: string[];
  logins: {
    name: string;
    email: string;
    machines: string[];
    /** Set once, on the commit that creates the login, and never again. */
    password: string | null;
  }[];
  existing_logins: string[];
}

export async function uploadFactoryManual(
  file: File,
  mode: "preview" | "commit",
  withLogins = true
): Promise<FactoryManualPlan> {
  const form = new FormData();
  form.append("file", file);
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(
    `${BASE}/api/admin/factory-manual/${mode}?with_logins=${withLogins}`,
    { method: "POST", headers, body: form }
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload?.detail === "string"
        ? payload.detail
        : `Upload failed (${response.status})`
    );
  }
  return payload as FactoryManualPlan;
}

export interface MaterialImport {
  rows: number;
  machines: Record<string, Record<Direction, number>>;
  errors: string[];
  saved: boolean;
}

export interface AdminMachine {
  id: number;
  code: string;
  name: string;
  stage_type: string;
  is_active: boolean;
  tolerance_pct: string;
  capacity_per_day: string | null;
  capacity_uom: string | null;
  materials: Record<Direction, string[]>;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: string;
  active: boolean;
  machines: string[];
}

// -- Reports ------------------------------------------------------------------

export interface DrillSpec {
  source: string;
  group: string[];
  filters: Record<string, unknown>;
  crumbs?: string[];
}

export interface DrillLevel {
  title: string;
  crumbs: string[];
  note: string;
  columns: { key: string; label: string; numeric: boolean }[];
  rows: {
    cells: Record<string, string | number | null>;
    drill?: DrillSpec;
    entry_id?: number;
  }[];
  totals: Record<string, string | number | null>;
  spec: DrillSpec;
}

export interface ReportCard {
  key: string;
  label: string;
  value: string | number | null;
  unit: string;
  note: string;
  drill: DrillSpec | null;
  tone: "warn" | null;
  /** How the figure is worked out — the card's (i). */
  info?: string;
  /** True where a fall is good: waste, exceptions. */
  inverted?: boolean;
  /** The same card for the period compared with. */
  previous?: string | number | null;
}

export interface Sums {
  runs: number;
  hours: string;
  manpower: number;
  kwh: string;
  input: string;
  output: string;
  loss: string;
  variance: string;
  variance_pct: string | null;
  out_of_tolerance: number;
}

export interface ProductionReport {
  period_from: string;
  period_to: string;
  totals: Sums;
  cards: ReportCard[];
  machines: (Sums & {
    machine_code: string;
    machine: string;
    tolerance_pct: string | null;
    drill: DrillSpec;
  })[];
  days: (Sums & { business_date: string; drill: DrillSpec })[];
  reasons: {
    reason: string;
    runs: number;
    variance: string;
    input: string;
    variance_pct: string | null;
    machines: string[];
    drill: DrillSpec;
  }[];
  loom: {
    loom_shifts: number;
    input_kg: string;
    output_kg: string;
    rolls: number;
    metres: string;
  };
  loom_cards: ReportCard[];
  previous_from: string | null;
  previous_to: string | null;
  plant_cards: ReportCard[];
  plant_days: (PlantDay & { business_date: string; drill: DrillSpec })[];
  plant_total: PlantDay;
  /** How each plant figure is worked out, by key. */
  plant_info: Record<string, string>;
}

/** One day of the plant, washing to dispatch. Dispatch is null on a
 *  one-shift view: a lorry has no shift. */
export interface PlantDay {
  washing_kg: string;
  rp_kg: string;
  agglo_kg: string;
  tape_kg: string;
  tape_waste_kg: string;
  tape_waste_pct: string | null;
  loom_mtr: string;
  loom_kg: string;
  loom_pct: string | null;
  loom_waste_kg: string;
  loom_waste_pct: string | null;
  stitching_pcs: string;
  printing_pcs: string;
  dispatch_pcs: string | null;
  dispatch_kg: string | null;
  manpower: number;
  kwh: string;
}

export interface EfficiencyRow {
  key: string;
  section: "Tape plant" | "RP" | "Loom" | "Dispatch";
  machine: string;
  unit: string;
  produced: string;
  days: number;
  avg_per_day: string | null;
  capacity_per_day: string | null;
  efficiency_pct: string | null;
  basis: string;
  info: string;
  drill: DrillSpec;
  previous_avg_per_day: string | null;
  previous_efficiency_pct: string | null;
}

export interface EfficiencyReport {
  period_from: string;
  period_to: string;
  previous_from: string | null;
  previous_to: string | null;
  working_days: number;
  cards: ReportCard[];
  rows: EfficiencyRow[];
  info: Record<string, string>;
}

export interface ExceptionsReport {
  period_from: string;
  period_to: string;
  previous_from: string | null;
  previous_to: string | null;
  cards: ReportCard[];
  negative_events: {
    business_date: string;
    item: string;
    item_code: string;
    location: string;
    available: string;
    requested: string;
    short_by: string;
    entered_by: string | null;
    doc_ref: string | null;
    run_id: number | null;
  }[];
  negative_now: {
    location: string;
    location_code: string;
    item: string;
    item_code: string;
    qty: string;
    uom: string;
    drill: DrillSpec;
  }[];
  out_of_tolerance: {
    run_id: number;
    business_date: string;
    shift: Shift;
    machine: string;
    variance_qty: string | null;
    variance_pct: string | null;
    tolerance_pct: string | null;
    reason: string | null;
    note: string | null;
    status: string;
  }[];
  changes: {
    at: string;
    action: string;
    user: string | null;
    reason: string | null;
    run_id: number;
    machine: string | null;
    business_date: string | null;
    shift: string | null;
  }[];
}
